import React, { useEffect, useState } from 'react'
import { SimpleBridgeBuilderForm } from '@features/nbee/SimpleBridgeBuilderForm'
import { useGetBridgeById } from '@app/api/getBridgeById'
import { useHistory, useParams } from 'react-router-dom'
import { useUpdateBridge } from '@app/api/updateBridge'
import { transformBridgeFormValuesToApiSaveBridgeBody } from '@app/api/utils/bridge'
import { appRoutes } from '@app/routes'
import { useAppDispatch } from '@app/store/hooks'
import { parseApiError } from '@app/api/utils/error'
import { sendToast } from '@app/store/actions/ApplicationConfigurationActions'
import { LoadingStep } from '@features/nbee/LoadingStep'
import { NbeeError } from '@features/nbee/NbeeError'
import { trackEvent } from '@app/dataTracking'
import { makeNbeeTrackingParams } from '@app/dataTracking/utils'
import { useSetupBridgeById } from '@app/api/postSetupBridge'
import { FormikHelpers } from 'formik'
import { BridgeFormValues } from 'Nbee'
import { useFetchAllApps } from '@app/api/getAllApps'
import { useGetUserInfo } from '@app/api/getUserInfo'
import { useGetUserModules } from '@app/api/getUserModules'

export const Step1Edit: React.FC = () => {
  const { bridgeId }: { bridgeId: string } = useParams()
  const history = useHistory()
  const dispatch = useAppDispatch()
  const [bridgeFormikProps, setBridgeFormikProps] = useState<{
    formValues: BridgeFormValues
    formikHelpers: FormikHelpers<BridgeFormValues>
  } | null>(null)

  const {
    data: allApps,
    isLoading: isLoadingApps,
    error: allAppsApiError,
  } = useFetchAllApps()

  const {
    data: bridgeFormValues,
    isLoading: isLoadingBridge,
    error: bridgeApiError,
  } = useGetBridgeById(bridgeId)

  const {
    data: user,
    isLoading: isLoadingUser,
    error: userError,
  } = useGetUserInfo()

  const {
    data: userModulesResponse,
    isLoading: isLoadingUserModules,
    error: apiUserModulesError,
  } = useGetUserModules()
  const userModules = userModulesResponse?.modules || []
  const emailReceiptUserModule = userModules.find(
    (m) => m.id === 'email-receipt'
  )

  const apiInitialDataError = allAppsApiError || bridgeApiError

  // Update bridge
  const {
    mutate: updateBridge,
    data: updateBridgeResponse,
    error: updateBridgeError,
    isLoading: isUpdatingBridge,
  } = useUpdateBridge()

  // Setup bridge
  const {
    mutate: setupBridge,
    data: bridgeSetupResponse,
    error: bridgeSetupApiError,
    isLoading: isSettingUpBridge,
  } = useSetupBridgeById()

  const isSavingApi = isSettingUpBridge || isUpdatingBridge
  const apiError = bridgeSetupApiError || updateBridgeError
  const updatedBridgeId = updateBridgeResponse?.data?.bridge?.id

  useEffect(() => {
    if (updatedBridgeId) {
      setupBridge({ bridgeId: updatedBridgeId })
    }
  }, [updatedBridgeId])

  // when setup bridge is completed we can redirect the user to next step
  useEffect(() => {
    if (bridgeSetupResponse) {
      history.push(appRoutes.nbeeStep2.makeUrl(bridgeId))
    }
  }, [bridgeSetupResponse])

  // if error we notify the user
  useEffect(() => {
    if (apiError) {
      const parsedError = parseApiError(apiError)
      dispatch(
        sendToast({
          title: 'Error',
          message: parsedError.message,
          color: 'negative',
        })
      )

      if (bridgeFormikProps) {
        bridgeFormikProps.formikHelpers.setSubmitting(false)

        trackEvent({
          eventName: 'UnexpectedErrorThrown',
          feature: 'NBEE',
          step: 'Apps',
          params: {
            ...makeNbeeTrackingParams(bridgeFormikProps.formValues),
            custom: {
              errorCode: parsedError.code,
              errorDescription: parsedError.message,
            },
          },
          sendEventToIntercom: true,
        })
      }
    }
  }, [apiError])

  return isLoadingBridge || isLoadingApps ? (
    <LoadingStep activeStepIndex={0} />
  ) : bridgeFormValues && allApps ? (
    <SimpleBridgeBuilderForm
      isSavingApi={isSavingApi}
      initialValues={bridgeFormValues}
      emailReceiptUserModule={emailReceiptUserModule}
      userData={user}
      onSubmit={(formValues, formikHelpers) => {
        const updateBridgeData = transformBridgeFormValuesToApiSaveBridgeBody(
          formValues,
          { step: 1 }
        )

        // we store all formik handlers in order to access them in the component scope
        setBridgeFormikProps({ formValues, formikHelpers })

        updateBridge({
          bridgeData: updateBridgeData,
          bridgeId,
        })
      }}
    />
  ) : apiInitialDataError ? (
    <NbeeError
      activeStepIndex={0}
      errorCode={parseApiError(apiInitialDataError).code}
      statusCode={parseApiError(apiInitialDataError).status}
      errorMessage={parseApiError(apiInitialDataError).message}
    />
  ) : null
}
